import type { DocNode } from '@atlaskit/adf-schema/doc';
import {
	AGENT_ATTRIBUTION_META,
	type AgentAttributionTransactionMeta,
} from '@atlaskit/editor-common/transaction-agent-attribution';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { JSONTransformer } from '@atlaskit/editor-json-transformer/JSONTransformer-2';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import type { NodeType, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Selection, Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { convertProsemirrorNodeToMarkdown } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';

import type { AIPlugin } from '../aiPluginType';
import type { EndSessionMetadata } from '../editor-actions/types';
import { aiSessionContextPluginKey } from '../pm-plugins/ai-session-context/ai-session-context-plugin-key';
import {
	AI_ORIGINAL_NODE_DATA,
	type AIOriginalNodeData,
} from '../pm-plugins/ai-session-context/meta-keys';
import {
	createEndAIExperienceCommand,
	createCommitInlineRovoStreamingCommand,
	createEndInlineRovoStreamingCommand,
	createOpenAIModalCommand,
	createShowInlineEditReadyCommand,
	createStageInlineRovoStreamingCommand,
	createStartAISessionCommand,
	createUpdateTargetIdCommand,
} from '../pm-plugins/decoration/actions';
import { createCommand, getPluginState } from '../pm-plugins/decoration/decoration-plugin-factory';
import type { AgentInfo } from '../pm-plugins/decoration/reducer';
import { endStream } from '../pm-plugins/pure-pm-in-editor-direct-streaming/transforms';
import type { AgentAttribution } from '../types/agent-attribution';
import type { AIGlobalOptIn } from '../types/types';
import { isInlineEditTargetInView as checkInlineEditTargetInView } from '../ui/components/InlineEditReadyBar/inline-edit-target';
import type { EditorPluginAIConfigItem } from '../ui/prebuilt/config-items/config-items';
import type { InvokedFrom } from '../ui/utils/analytics/analyticsFlowTypes';
import { isPromptUpdatesEnabled } from '../ui/utils/prompt-updates-enabled';

const isEditorAgentMentionsExperimentEnabled = (): boolean =>
	expValEquals('platform_editor_agent_mentions', 'isEnabled', true);

export const openAIModal = ({
	invokedFrom,
	aiOptIn,
	aiSessionContextType,
	agentInfo,
	configItem,
	editorView,
	triggeredFor,
	isExternal = false,
	withAcceptReject = true,
	withStreamingHighlight = true,
	hideLoadingScreen = false,
	traceId,
	singleInstrumentationId,
	isCreate,
	promptVersion,
	userADFInput,
	overrideFromPos,
	overrideToPos,
	shouldExpandPositions,
	skipSelectionHighlight,
	preserveModalMountTimestamp,
}: {
	/** Forwarded to `createOpenAIModalCommand`. See its doc comment. */
	agentInfo?: AgentInfo;
	aiOptIn: AIGlobalOptIn;
	aiSessionContextType?: { type: 'PROMPT' } | { type: 'STREAM' };
	configItem: EditorPluginAIConfigItem;
	editorView?: EditorView;
	hideLoadingScreen?: boolean;
	invokedFrom: InvokedFrom;
	isCreate?: boolean;
	isExternal?: boolean;
	/**
	 * Optional override for the modal-anchor start position. When
	 * omitted the modal is anchored at the current selection.
	 */
	overrideFromPos?: number;
	/** Companion to `overrideFromPos`. */
	overrideToPos?: number;
	// Forwarded to `createOpenAIModalCommand`; when true the modal re-anchors without remounting.
	preserveModalMountTimestamp?: boolean;
	promptVersion?: string;
	/** Forwarded to `createOpenAIModalCommand`. */
	shouldExpandPositions?: boolean;
	singleInstrumentationId?: string;
	/**
	 * When true, the modal is still decorated and anchored but the range
	 * selection highlight is omitted.
	 */
	skipSelectionHighlight?: boolean;
	traceId?: string;
	triggeredFor?: NodeType;
	userADFInput?: JSONDocNode;
	withAcceptReject?: boolean;
	withStreamingHighlight?: boolean;
}): void => {
	if (!editorView) {
		return;
	}

	// Canonical enrolment point for the prompt-updates experiment. Every site that
	// reads the treatment (modal inset, re-centre offset, prompt placeholder,
	// inline agent selector) runs on a render path and therefore uses the
	// no-exposure variant, so the exposure is logged here instead — once per modal
	// open. The return value is intentionally unused.
	isPromptUpdatesEnabled();

	const openAIModalCommand = createOpenAIModalCommand({
		state: editorView.state,
		configItem,
		agentInfo,
		lastTriggeredFrom: invokedFrom,
		aiGlobalOptIn: aiOptIn,
		aiSessionContextType,
		triggeredFor,
		isExternal,
		withAcceptReject,
		withStreamingHighlight,
		hideLoadingScreen,
		traceId,
		singleInstrumentationId,
		isCreate,
		promptVersion,
		userADFInput,
		overrideFromPos,
		overrideToPos,
		shouldExpandPositions,
		skipSelectionHighlight,
		preserveModalMountTimestamp,
	});

	openAIModalCommand(editorView.state, editorView.dispatch);
};

export const endAIExperience = ({ editorView }: { editorView?: EditorView }): void => {
	if (!editorView) {
		return;
	}

	const endAIExperienceCommand = createEndAIExperienceCommand();
	endAIExperienceCommand(editorView.state, editorView.dispatch);
};

export const startAISession = ({
	editorView,
	sessionId,
	traceId,
	promptVersion,
	agentInfo,
	agentAttribution,
	isCreateSession,
	isInlineEditSession,
	isChatEditSession,
	sourceMessageId,
}: {
	agentAttribution?: AgentAttribution;
	agentInfo?: AgentInfo;
	editorView?: EditorView;
	/**
	 * True when the originating edit was from Rovo chat (`chat_edit` or
	 * Create-with-Rovo). Currently unread (the only reader was Review moment
	 * code); kept because removing it would change the public `startSession`
	 * action signature, which ripples into `editor-plugin-ai-streaming-orchestrator`.
	 */
	isChatEditSession?: boolean;
	/** True for a create stream. Currently unread for the same reason as `isChatEditSession`. */
	isCreateSession?: boolean;
	/** True for an inline-edit stream. Currently unread for the same reason as `isChatEditSession`. */
	isInlineEditSession?: boolean;
	promptVersion?: string;
	sessionId: string;
	/**
	 * Assistant message that started this chat-edit (`singleInstrumentationId`).
	 * Currently unread for the same reason as `isChatEditSession`.
	 */
	sourceMessageId?: string;
	traceId?: string;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}

	// agentInfo is intentionally gated behind the experiment flag.
	// When the experiment is off, agentInfo is silently discarded so that
	// downstream code never receives agent identity data in the session state.
	const agentInfoForSession = isEditorAgentMentionsExperimentEnabled() ? agentInfo : undefined;

	return createStartAISessionCommand(
		sessionId,
		traceId,
		fg('platform_editor_ai_promptversion_in_analytics') ? promptVersion : undefined,
		agentInfoForSession,
		isCreateSession,
		isInlineEditSession,
		agentAttribution,
		isChatEditSession,
		sourceMessageId,
	)(editorView.state, editorView.dispatch);
};

export const endAISession = ({
	api,
	editorView,
	sessionId,
	metadata,
	baseGenerate,
	aiOptIn,
}: {
	/**
	 * Currently unread (the only reader was the Review moment auto-open). Kept so
	 * this signature stays stable for its one caller.
	 */
	aiOptIn?: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	/**
	 * Currently unread (the only reader was the Review moment auto-open). Kept so
	 * this signature stays stable for its one caller.
	 */
	baseGenerate?: EditorPluginAIConfigItem;
	editorView?: EditorView;
	metadata: EndSessionMetadata;
	sessionId: string;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}

	// [platform_editor_remove_pause_streaming] When aborting during streaming
	// (e.g. from the Rovo sidebar stop button), dispatch COMPLETE to commit
	// partial content, and endStream to re-enable the editor.
	// For the inline stop path, Loading.tsx dispatches
	// these BEFORE calling abort(), so status is already 'complete' here and
	// this block is skipped.
	if (
		metadata?.type === 'abort' &&
		expValEqualsNoExposure('platform_editor_remove_pause_streaming', 'isEnabled', true) &&
		api
	) {
		commitPartialStreamAndCleanup({ api, editorView });
	}

	const endSessionCommand = createCommand(
		(state) => {
			const aiDecorationExperiencePluginState = getPluginState(state);
			const nextSessionId = aiDecorationExperiencePluginState.nextSessionId;

			// If a session which is not the next session is trying to be ended then we should abort trying to end the session.
			// An empty sessionId (from user-initiated stop) bypasses this check
			// since the session ID may not be available in all streaming entry points.
			if (sessionId && sessionId !== nextSessionId) {
				return false;
			}

			return {
				type: 'END_SESSION',
				data: { sessionId: undefined, type: metadata?.type },
			};
		},
		(tr) => {
			if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
				tr.setMeta(aiSessionContextPluginKey, { type: 'RESET' });
			}

			return tr;
		},
	);

	return endSessionCommand(editorView.state, editorView.dispatch);
};

export const updateTargetId = ({
	editorView,
	targetId,
}: {
	editorView?: EditorView;
	targetId: string | number;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}
	return createUpdateTargetIdCommand(targetId)(editorView.state, editorView.dispatch);
};

export const stageInlineRovoStreaming = ({
	editorView,
	targetId,
}: {
	editorView?: EditorView;
	targetId: string;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}
	return createStageInlineRovoStreamingCommand(targetId)(editorView.state, editorView.dispatch);
};

export const commitInlineRovoStreaming = ({
	editorView,
}: {
	editorView?: EditorView;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}
	return createCommitInlineRovoStreamingCommand()(editorView.state, editorView.dispatch);
};

export const endInlineRovoStreaming = ({
	editorView,
}: {
	editorView?: EditorView;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}
	return createEndInlineRovoStreamingCommand()(editorView.state, editorView.dispatch);
};

export const showInlineEditReady = ({
	editorView,
	targetId,
}: {
	editorView?: EditorView;
	targetId?: string;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}
	return createShowInlineEditReadyCommand(targetId)(editorView.state, editorView.dispatch);
};

export const isInlineEditTargetInView = ({
	editorView,
	targetId,
}: {
	editorView?: EditorView;
	targetId?: string;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}
	return checkInlineEditTargetInView({ editorView, targetId });
};

interface GetPartialPMNodeFromSelectionOptions {
	maxSize?: number;
	selection: Selection | undefined;
}

export const getPartialPMNodeFromSelection: (
	options: GetPartialPMNodeFromSelectionOptions,
) => PMNode | undefined = ({ selection, maxSize = 10000 }) => {
	if (!selection) {
		return undefined;
	}

	const { $from, $to } = selection;

	const doc = $from.doc;

	/**
	 * Check the from and to positions of the lowest depth level (doc level).
	 * If the size is over the threshold, increase the depth.
	 */

	let depth = 0;

	while (
		// Check until max depth is reached from the resolved positions
		!isNaN($from.start(depth)) &&
		!isNaN($to.end(depth))
	) {
		const from = $from.start(depth);
		const to = $to.end(depth);
		if (to - from < maxSize) {
			break;
		}
		depth++;
	}

	if (depth === 0) {
		return doc;
	}

	// find a depth that fits and then start looking at siblings n - 1 and n + 1 and so on

	const parentDepth = depth - 1;
	const index = $from.index(parentDepth);

	let from = $from.start(depth);
	let to = $to.end(depth);
	let increment = 1;

	const maxIndex = $from.node(parentDepth).childCount;

	while (
		// Loop until both ends of the bounds have been reached
		index - increment >= 0 ||
		index + increment <= maxIndex
	) {
		const newFrom = $from.posAtIndex(Math.max(index - increment, 0), parentDepth);
		// We look ahead at the next index as posAtIndex yields the starting position of the node
		const newTo = $to.posAtIndex(Math.min(index + increment + 1, maxIndex), parentDepth);

		let isPositionUpdated = false;
		if (to - newFrom < maxSize) {
			from = newFrom;
			isPositionUpdated = true;
		}
		if (newTo - from < maxSize) {
			to = newTo;
			isPositionUpdated = true;
		}

		if (!isPositionUpdated) {
			break;
		}
		increment++;
	}

	return doc.cut(from, to);
};

export const getPartialDocumentFromSelection: (
	options: GetPartialPMNodeFromSelectionOptions,
) => DocNode | undefined = (options) => {
	const transformer = new JSONTransformer();
	const doc = getPartialPMNodeFromSelection(options);
	if (doc) {
		return transformer.encode(doc) as DocNode;
	}
};

export const getPartialMarkdownFromSelection: (
	options: GetPartialPMNodeFromSelectionOptions,
) => string | undefined = (options) => {
	const doc = getPartialPMNodeFromSelection(options);
	if (doc) {
		return (
			convertProsemirrorNodeToMarkdown({
				node: doc,
				featureToggles: {
					markdownPlusSanitizeProseMirrorOnlyNodes: fg(
						'platform_editor_markdown_plus_sanitize_pm_nodes',
					),
				},
			})?.markdown ?? ''
		);
	}
};

export const tagAsAIStreamingTransformation = (
	tr: Transaction,
	options?: {
		agentAttribution?: AgentAttributionTransactionMeta;
		isFinal: boolean;
	},
): Transaction => {
	if (options?.isFinal) {
		tr.setMeta('isAIFinalStreamingTransformation', true);
	} else {
		tr.setMeta('isAIStreamingTransformation', true);
	}
	if (fg('confluence_ncs_step_diffing_version_history') && options?.agentAttribution) {
		tr.setMeta(AGENT_ATTRIBUTION_META, options.agentAttribution);
	}
	return tr;
};

/**
 * Record the exact node a `replaceNode` command located, so
 * `buildMountTransaction` can rebuild an accurate single-node
 * `originalUserContentSlice` instead of relying on the doc-range slice.
 *
 * Stamps `AI_ORIGINAL_NODE_DATA`, which `apply()` stores on the
 * `replaceNodeOriginalNode` session-state field.
 */
export const recordOriginalNode = (tr: Transaction, originalNode: PMNode): Transaction => {
	const data: AIOriginalNodeData = { originalNode };
	return tr.setMeta(AI_ORIGINAL_NODE_DATA, data);
};

/**
 * [platform_editor_remove_pause_streaming]
 * Commits partial AI-streamed content and cleans up streaming state.
 *
 * 1. COMPLETE — triggers handleFinalize (restore → commit), keeping partial
 *    content in the document.
 * 2. endStream — resets the streaming plugin (isStreaming/isStreamingFinished)
 *    so the editor is re-enabled (Update button becomes clickable).
 */
export function commitPartialStreamAndCleanup({
	api,
	editorView,
}: {
	api: ExtractInjectionAPI<AIPlugin>;
	editorView: EditorView;
}): void {
	const sessionState = aiSessionContextPluginKey.getState(editorView.state);
	if (!sessionState || sessionState.status !== 'streaming') {
		return;
	}

	api.aiExperience.actions.dispatchAIEvent({
		type: 'complete',
		adfResponse: sessionState.adfResponse,
		markdown: sessionState.markdown,
	});

	const tr = editorView.state.tr;
	endStream(tr);
	editorView.dispatch(tr);
}
